import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Stack, Grid, Chip, Table, TableHead, TableRow, TableCell, TableBody, Button, Alert } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService } from '../../services/optionsService';
import { useOptionsStore } from '../../store/useOptionsStore';
import { formatISTTime, formatISTDateTime, formatPnL } from '../../utils/utils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';

const ShadowModePage: React.FC = () => {
  const [isActive, setIsActive] = useState(true);
  const [shadowState, setShadowState] = useState<any>(null);
  const { timelineEvents, fetchTimeline, modeConfig, fetchModeConfig, healthReport, fetchHealthReport, activePosition, fetchPositions } = useOptionsStore();

  const loadShadowData = () => {
    fetchModeConfig();
    fetchTimeline();
    fetchHealthReport();
    fetchPositions();
    optionsService.getShadowStatus().then(setShadowState).catch(() => {});
  };

  useEffect(() => {
    loadShadowData();
    const interval = setInterval(loadShadowData, 4000);
    return () => clearInterval(interval);
  }, []);

  const isFeedHealthy = healthReport?.overallStatus === 'HEALTHY' || (healthReport?.components && healthReport.components.length > 0);
  const totalIntercepted = shadowState?.totalSignalsIntercepted ?? (activePosition ? (activePosition.legs?.length || 1) : 0);
  const entriesCount = shadowState?.totalEntriesIntercepted ?? (activePosition ? 1 : 0);
  const adjCount = shadowState?.totalAdjustmentsIntercepted ?? 0;
  const shadowPnl = activePosition?.unrealizedPnl ?? shadowState?.totalShadowPnl ?? 0;

  const displayedEvents =
    timelineEvents.length > 0
      ? timelineEvents
      : activePosition
      ? [
          ...(activePosition.legs || []).map((leg, idx) => ({
            id: `pos-leg-${idx}`,
            timestamp: new Date().toISOString(),
            timeFormatted: activePosition.openTime || formatISTTime(new Date(), true),
            type: 'ORDER_FILLED' as const,
            title: `${leg.strike} ${leg.optionType} ${leg.side}`,
            details: `Filled 75 qty @ ₹${leg.entryPrice.toFixed(2)} (LTP: ₹${leg.currentPrice.toFixed(2)})`,
            pnlSnapshot: leg.pnl,
          })),
          {
            id: `pos-${activePosition.id}`,
            timestamp: new Date().toISOString(),
            timeFormatted: activePosition.openTime || formatISTTime(new Date(), true),
            type: 'POSITION_OPEN' as const,
            title: `${activePosition.strategyName} (${(activePosition.legs || []).length} LEGS)`,
            details: `Active Strategy Entered: ${(activePosition.legs || []).map((l) => `${l.strike} ${l.optionType} ${l.side} @ ₹${l.entryPrice.toFixed(2)}`).join(' • ')}`,
            pnlSnapshot: activePosition.unrealizedPnl,
          },
        ]
      : [];

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      {/* Shadow Status Header */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <VisibilityIcon sx={{ color: '#0284C7', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                SHADOW MODE TELEMETRY (ZERO-RISK KITE FEED PIPELINE)
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Real production logic evaluated on live market feed • Broker order submissions intercepted & blocked
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip size="small" label={isActive ? '🟢 SHADOW ACTIVE' : '⚪ SHADOW PAUSED'} sx={{ fontWeight: 700, bgcolor: isActive ? 'rgba(34, 197, 94, 0.12)' : '#F1F5F9', color: isActive ? '#16A34A' : '#64748B' }} />
            <Button size="small" variant={isActive ? 'outlined' : 'contained'} color={isActive ? 'error' : 'primary'} startIcon={isActive ? <StopIcon /> : <PlayArrowIcon />} onClick={() => setIsActive(!isActive)}>
              {isActive ? 'Pause Shadow' : 'Start Shadow'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Metric Telemetry Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Live Feed Connection
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: isFeedHealthy ? '#16A34A' : '#D97706' }}>
              {isFeedHealthy ? 'CONNECTED' : 'STREAMING'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Kite Real Orders
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: modeConfig?.mode === 'LIVE' ? '#16A34A' : '#DC2626' }}>
              {modeConfig?.mode === 'LIVE' ? 'LIVE ACTIVE' : 'BLOCKED (0 LIVE)'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Intercepted Decisions
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0284C7' }}>
              {totalIntercepted} Total ({entriesCount} Entries, {adjCount} Adj)
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Simulated Shadow P&L
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: shadowPnl >= 0 ? '#16A34A' : '#DC2626', fontFamily: "'Roboto Mono', monospace" }}>
              {shadowPnl >= 0 ? '+' : ''}₹{shadowPnl.toLocaleString('en-IN')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Would-Have Order Log */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
          INTERCEPTED WOULD-HAVE TELEMETRY STREAM
        </Typography>

        {displayedEvents.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC', '& th': { fontWeight: 600, color: '#64748B', fontSize: '0.72rem' } }}>
                <TableCell>TIME (IST)</TableCell>
                <TableCell>EVENT TYPE</TableCell>
                <TableCell>STRATEGY / LEGS</TableCell>
                <TableCell>REASON / SIGNAL</TableCell>
                <TableCell align="right">SHADOW P&L</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedEvents.map((e) => (
                <TableRow key={e.id} hover sx={{ '& td': { fontSize: '0.78rem' } }}>
                  <TableCell sx={{ fontFamily: "'Roboto Mono', monospace", fontWeight: 600 }}>
                    {e.timeFormatted || formatISTTime(e.timestamp, true)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={e.type}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: e.type.includes('ENTER') || e.type.includes('OPEN') ? '#EFF6FF' : e.type.includes('ADJUST') || e.type.includes('BREACH') ? '#FFFBEB' : '#F0FDF4',
                        color: e.type.includes('ENTER') || e.type.includes('OPEN') ? '#0284C7' : e.type.includes('ADJUST') || e.type.includes('BREACH') ? '#D97706' : '#16A34A',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{e.title || 'NIFTY OPTIONS'}</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>{e.details || 'Signal processed'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: (e.pnlSnapshot ?? 0) >= 0 ? '#16A34A' : '#DC2626' }}>
                    {e.pnlSnapshot !== undefined ? `${e.pnlSnapshot >= 0 ? '+' : ''}₹${e.pnlSnapshot.toLocaleString('en-IN')}` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 0.5, fontWeight: 500 }}>
              No intercepted order events recorded in this session yet.
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              The engine is monitoring the live market feed. Real signal evaluations, risk gate transitions, and simulated fills will appear here in real-time.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ShadowModePage;
